/* @flow */

import * as React from 'react';

import type { FeedRequestResponseType } from '../../libs/netgemLibrary/v8/requests/feed';
import type { NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import type { UserDeviceInfoType } from '../../redux/appRegistration/types/types';

import AccurateTimestamp from '../../helpers/dateTime/AccurateTimestamp';
import { logError } from '../../helpers/debug/debug';
import { type AllSettledPromises, SettledPromiseFulfilled } from '../../helpers/jsHelpers/promise';
import { fetchJsonWithErrorCheck } from '../../helpers/jsHelpers/xhr';
import Localizer from '../../helpers/localization';
import { StorageKeys } from '../../helpers/localStorage/keys';
import LocalStorageManager from '../../helpers/localStorage/localStorageManager';
import { PendingOperationKind, updatePendingOperationType } from '../../helpers/rights/pendingOperations';
import { buildFeedItem } from '../../libs/netgemLibrary/v8/helpers/Item';
import { type STEP_ITEM, StepName } from './StartViewConstsAndTypes';

const getBooleanValue = (value: string): ?boolean => {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  }

  return null;
};

const parseUrl = (url: string, defaultValues: UserDeviceInfoType): UserDeviceInfoType => {
  const parameters = new URLSearchParams(url.slice(url.indexOf('?') + 1));

  const userDeviceInfoKeys = Object.keys(defaultValues);
  const lcUserDeviceInfoKeys = userDeviceInfoKeys.map((value) => value.toLowerCase());
  const userDeviceInfo: UserDeviceInfoType = { ...defaultValues };

  for (const [key, value] of parameters.entries()) {
    const lcKey = key.toLowerCase();

    // For registration pages not providing the right case for keys
    const i = lcUserDeviceInfoKeys.indexOf(lcKey);
    const boolValue = getBooleanValue(value);

    if (i >= 0) {
      (userDeviceInfo as Object)[userDeviceInfoKeys[i]] = boolValue !== null ? boolValue : value;
    } else if (lcKey === 'subscriptionskipped' && boolValue === true) {
      // User skipped subscription: no need to retry to play
      updatePendingOperationType(PendingOperationKind.OpenCard);
    }
  }

  // Remove data from local storage, so it's not reused later
  LocalStorageManager.delete(StorageKeys.CrmData);

  return userDeviceInfo;
};

const isBOStepEnabled = (name: string): boolean => name !== StepName.VideofuturAuthentication;

const filterCurrentSteps = (currentSteps: Array<STEP_ITEM>, disabledSteps: Array<STEP_ITEM> | STEP_ITEM): Array<STEP_ITEM> => {
  const localDisabledSteps = Array.isArray(disabledSteps) ? disabledSteps : [disabledSteps];

  // Remove disabled steps from current steps
  const newSteps = currentSteps.filter(({ name }) => localDisabledSteps.every((s) => s.name !== name));

  // Add children processes of disabled steps to current steps
  localDisabledSteps.forEach((step) => {
    if (step.then) {
      newSteps.push(...step.then);
    }
  });

  return newSteps;
};

const renderTextLink = (text: string, url: string): React.Node => (
  <a href={url} rel='noopener noreferrer' target='_blank'>
    {text}
  </a>
);

const renderImageLink = (image: string | null, url: string, ariaLabelKey: string): React.Node => (
  <a aria-label={Localizer.localize(ariaLabelKey)} href={url} rel='noopener noreferrer' target='_blank'>
    <img alt='' src={image} />
  </a>
);

const getCrmVersion = async (urlStr: ?string, updater: (version: string) => void, kind: string): Promise<void> => {
  if (!urlStr) {
    return;
  }

  const isAbsoluteUrl = urlStr.startsWith('http');
  const url = new URL(isAbsoluteUrl ? urlStr : window.location.origin);

  if (!isAbsoluteUrl) {
    url.pathname = urlStr;
  }

  // Ensure latest version is retrieved
  url.searchParams.set('v', AccurateTimestamp.now().toString());

  try {
    const { version } = await fetchJsonWithErrorCheck(url, { mode: 'cors' });
    if (version) {
      updater(version);
    }
  } catch (error) {
    // Error 404 is ignored because there's no CRM version for some apps (myVF, ZEOP, etc.)
    if (error.message !== '404') {
      logError(`Error retrieving CRM ${kind} version: ${error.message}`);
    }
  }
};

const getItem: (results: AllSettledPromises<FeedRequestResponseType>) => NETGEM_API_V8_FEED_ITEM | null = (results) => {
  for (const { status, value } of results) {
    if (status === SettledPromiseFulfilled && value) {
      const {
        result: {
          feed: [rawItem],
        },
      } = value;

      if (rawItem) {
        const item = buildFeedItem({
          ...rawItem,
          score: [0],
        });

        if (item) {
          return item;
        }
      }
    }
  }

  return null;
};

export { filterCurrentSteps, getCrmVersion, getItem, isBOStepEnabled, parseUrl, renderImageLink, renderTextLink };
